/*
 * Copyright (c) 2024 metromorph softworks GmbH
 *
 * All rights reserved
 */

import * as React from "react";

import { withTranslation } from "react-i18next";

import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
 
import AppContext from "../AppContext";

export type ErrorBoundaryProps = {
    readonly children: React.ReactNode,
    readonly t: (text: string) => string, //eslint-disable-line no-unused-vars
}

type ErrorState = {
    error?: Error,
    errorInfo?: React.ErrorInfo
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorState> {
    /* eslint-disable react/static-property-placement */
    static contextType = AppContext; 
    context: React.ContextType<typeof AppContext> = {}; 
    /* eslint-enable react/static-property-placement */


    state: ErrorState = { };

    static getDerivedStateFromError(error: Error) {
        // Update state so the next render will show the fallback UI.
        return { error } as ErrorState;  
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {    
        // You can also log the error to an error reporting service
        this.setState({...this.state, error, errorInfo });
    }

    resetError() {
        if (this.context?.home) {
            this.setState({ error: undefined, errorInfo: undefined });
            this.context.home?.();
        }
    }

    render() {
        const { t } = this.props;
        if (this.state.error) {    
            return (
                <Container>
                    <Typography mt={4} variant="h4">
                        {t("Unerwarteter Fehler")}
                    </Typography>
                    <pre>
                        {this.state.error?.message}
                    </pre>
                    {this.state.errorInfo ? (
                        <pre>
                            {this.state.errorInfo?.componentStack}
                        </pre>
                    ) : null}
                    {this.context?.home ? (
                        <Box mt={1}>
                            <Button 
                                onClick={() => this.resetError()}
                            >
                                {t("Fehler zurücksetzen")}
                            </Button>
                        </Box>
                    ) : null}
                </Container>
            );
        }
        return this.props.children; 
    }
}

export default withTranslation()(ErrorBoundary);
